import React, { useMemo, useState } from 'react'
import {
  SectionHeader,
  SectionHeaderWrapper
} from 'features/bpDetails/BpDetails'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { getFileTypes } from 'ducks/files/selectors'
import { isEmpty, pathOr, propOr } from 'ramda'
import { getFileTypeName } from 'utils/bpData'
import FileCategories from 'features/bpDetails/Components/files/FileCategories'
import { selectSelectedUnitFiles } from 'ducks/units/selectors'
import UnitFolders from 'features/bpDetails/Components/units/UnitFolders'
import { isResidentialUnit } from 'utils/units'
import File from 'features/bpDetails/Components/File'
import EmptyFileState from 'features/bpList/Components/EmptyFileState'

import BulkShareFilesModal from '../../../../features/bpDetails/Components/ShareBulkFilesModal'
import BulkEditFileModal from '../../../../features/bpDetails/Components/EditBulkFilesModal'
import BulkUnshareFilesModal from '../../../../features/bpDetails/Components/UnshareBulkFilesModal'

import EditIcon from '@mui/icons-material/Edit'
import ShareIcon from '@mui/icons-material/Share'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { IconButton, Tooltip, Checkbox } from '@mui/material'

const UnitFiles = ({ unit, canEdit }) => {
  const unitFiles = useSelector(selectSelectedUnitFiles)
  const fileTypes = useSelector(getFileTypes)
  const [recentFilesVisible, setRecentFilesVisible] = useState(true)
  const isResidential = isResidentialUnit(unit.buildingType)

  const [selectedFiles, setSelectedFiles] = useState([])

  const [bulkShareModalOpen, setBulkShareModalOpen] = useState(false)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
  const [bulkUnshareModalOpen, setBulkUnshareModalOpen] = useState(false)

  const handleSelectFile = (fileId, isChecked) => {
    setSelectedFiles(prev =>
      isChecked ? [...prev, fileId] : prev.filter(id => id !== fileId)
    )
  }

  const recentFiles = useMemo(() => {
    if (unitFiles && unit) {
      const shared = propOr([], 'sharedFiles', unit).map(u => ({
        ...u,
        isShared: true
      }))
      const privateFiles = propOr([], 'privateFiles', unit).map(u => ({
        ...u,
        isPrivate: true
      }))
      const allFiles = [...unitFiles, ...shared, ...privateFiles]
      const sortedFiles = allFiles.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )
      return sortedFiles.slice(0, 3)
    }
    return []
  }, [unitFiles, unit])

  const recentFileIds = useMemo(() => (recentFiles || []).map(f => f.id), [recentFiles])

  const allSelected = recentFileIds.length > 0 && recentFileIds.every(id => selectedFiles.includes(id))
  const indeterminate = selectedFiles.some(id => recentFileIds.includes(id)) && !allSelected

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedFiles(prev => prev.filter(id => !recentFileIds.includes(id)))
    } else {
      setSelectedFiles(prev => [...new Set([...prev, ...recentFileIds])])
    }
  }

  return (
    <UnitFilesWrapper>
      {recentFilesVisible && (
        <>
          <SectionHeaderWrapper>
            <SectionHeader>Recent files</SectionHeader>
          </SectionHeaderWrapper>
          {selectedFiles.length > 0 && (
            <BulkActionsBar>
              <Tooltip title="Edit selected files">
                <IconButton onClick={() => setBulkEditModalOpen(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Share selected files">
                <IconButton onClick={() => setBulkShareModalOpen(true)}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Unshare selected files">
                <IconButton onClick={() => setBulkUnshareModalOpen(true)}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </BulkActionsBar>
          )}

          <FilesWrapper>
            {isEmpty(recentFiles) ? (
              <EmptyFileState
                text={
                  canEdit && !isResidential
                    ? 'Add your first file to this Unit'
                    : 'There are no recent files'
                }
                buttonText='+ Add files'
                hideButton={!canEdit || isResidential}
                unit={unit}
              />
            ) : (
              <>
                <FilesHeaderRow>
                  <HeaderCell style={{ width: '32px', padding: '0', justifyContent: 'flex-start' }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={indeterminate}
                      onChange={handleSelectAll}
                      sx={{
                        padding: 0,
                        marginLeft: '-2px',
                        color: '#b0b0b0',
                        '&.Mui-checked': {
                          color: '#58bca7',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: '#58bca7',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(88, 188, 167, 0.04)'
                        }
                      }}
                    />
                  </HeaderCell>
                  <HeaderCell style={{ flex: 2, minWidth: '150px' }}>Name</HeaderCell>
                  <HeaderCell style={{ flex: 1.5, minWidth: '120px', maxWidth: '315px' }}>
                    Type
                  </HeaderCell>
                  <HeaderCell style={{ flex: 1, minWidth: '30px', maxWidth: '72px' }}>
                    Uploaded
                  </HeaderCell>
                  <HeaderCell style={{ flex: 1, minWidth: '30px', maxWidth: '74px' }}>
                    Uploader
                  </HeaderCell>
                  <HeaderCell style={{ width: '60px' }} />
                </FilesHeaderRow>

                {recentFiles.map((file, idx) => (
                  <File
                    key={file.id}
                    isUnit
                    isShared={file.isShared}
                    file={file}
                    isPrivate={file.isPrivate}
                    unit={unit}
                    type={getFileTypeName(
                      pathOr('', ['fileType', 'id'], file),
                      fileTypes
                    )}
                    isSelected={selectedFiles.includes(file.id)}
                    onSelect={handleSelectFile}
                    rowIndex={idx}
                    viewMode="list"
                  />
                ))}
              </>
            )}
          </FilesWrapper>
        </>
      )}
      <UnitFolders unit={unit} />
      {!isResidential && (
        <FileCategories
          unit={unit}
          setRecentFilesVisible={setRecentFilesVisible}
          canEditUnit={canEdit}
        />
      )}
      <BulkShareFilesModal
        open={bulkShareModalOpen}
        onClose={() => setBulkShareModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />

      <BulkEditFileModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />

      <BulkUnshareFilesModal
        open={bulkUnshareModalOpen}
        onClose={() => setBulkUnshareModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />
    </UnitFilesWrapper>
  )
}

export default UnitFiles

const UnitFilesWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  margin-top: 30px;
`

const FilesWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  margin-bottom: 5px;
`

const FilesHeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 8px 2px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-sizing: border-box;
  margin-top: 10px;
`

const HeaderCell = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BulkActionsBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin: 10px 0;
`
